// src/features/bookings/components/BookingDetail/DeliverablesTable.jsx
// ✅ Timeline Design — مراحل العمل كخط زمني مع تفرع المهام

import React, { useState, useEffect } from 'react';
import api from '../../../../api/axiosInstance';
import { useAuth } from '../../../auth/context/AuthContext';
import TaskAttachmentModal from '../../../tasks/components/TaskAttachmentModal';
import BulkAddTaskModal from '../../../tasks/components/BulkAddTaskModal';
import styles from './DeliverablesTable.module.css';

// ─── ثوابت ──────────────────────────────────────────────────
const STATUS_MAP = {
  todo:        { label: 'مطلوبة',      cls: 'labelTodo',       dotCls: 'dotTodo'       },
  in_progress: { label: 'قيد التنفيذ', cls: 'labelInProgress', dotCls: 'dotInProgress' },
  done:        { label: 'مكتملة',      cls: 'labelDone',       dotCls: 'dotDoneTask'   },
  cancelled:   { label: 'ملغاة',       cls: 'labelCancelled',  dotCls: 'dotCancelled'  },
};

const PRIORITY_MAP = {
  high:   { label: '⭐⭐ عالية',  cls: 'priorityHigh'   },
  medium: { label: '⭐ متوسطة',   cls: 'priorityMedium' },
  low:    { label: 'منخفضة',      cls: 'priorityLow'    },
};

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric',
    });
  } catch { return d; }
};

const getUserName = (u) => {
  if (!u) return null;
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
};

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width;
        c.height = img.height;
        c.getContext('2d').drawImage(img, 0, 0);
        c.toBlob(
          (b) => resolve(b ? new File([b], file.name, { type: 'image/jpeg' }) : file),
          'image/jpeg', 0.8
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// ─── هل اكتملت المرحلة؟ ─────────────────────────────────────
const isStageComplete = (stage) =>
  stage.is_shot && stage.is_edited && stage.is_posted;

const isStageActive = (stage) => {
  const tasks = stage.tasks || [];
  return tasks.some(t => t.status === 'in_progress') && !isStageComplete(stage);
};

// ─── بطاقة مهمة ──────────────────────────────────────────────
function TaskCard({ task, users, canChange, canDelete, onUpdate, onDelete, onAttachment }) {
  const st  = STATUS_MAP[task.status]     || { label: task.status,   cls: 'labelTodo',   dotCls: 'dotTodo' };
  const pri = PRIORITY_MAP[task.priority] || { label: task.priority, cls: 'priorityLow' };
  const done = task.status === 'done';

  return (
    <div className={`${styles.taskCard} ${done ? styles.taskDone : ''}`}>

      {/* زر الحذف — يظهر عند hover */}
      {canDelete && (
        <button
          className={styles.deleteBtn}
          type="button"
          onClick={() => onDelete(task.id, task.title)}
          title="حذف"
        >
          ×
        </button>
      )}

      {/* السطر الأول */}
      <div className={styles.taskRow1}>
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={done}
          onChange={(e) => onUpdate(task.id, 'status', e.target.checked ? 'done' : 'in_progress')}
          disabled={!canChange || task.status === 'cancelled'}
          title={done ? 'إعادة فتح' : 'تأشير كمكتملة'}
        />

        {/* نقطة الحالة */}
        <span className={`${styles.statusDot} ${styles[st.dotCls]}`} />

        {/* العنوان */}
        <span className={`${styles.taskTitle} ${done ? styles.taskTitleDone : ''}`}>
          {task.title}
        </span>

        {/* شارة الحالة */}
        <span className={`${styles.statusLabel} ${styles[st.cls]}`}>
          {st.label}
        </span>

        {/* الأولوية */}
        <span className={`${styles.priorityBadge} ${styles[pri.cls]}`}>
          {pri.label}
        </span>

        {/* زر المرفق */}
        {task.attachment && (
          <button
            className={styles.attachBtn}
            type="button"
            onClick={() => onAttachment(task.attachment, task.title)}
          >
            📎 مرفق
          </button>
        )}
      </div>

      {/* السطر الثاني — التواريخ */}
      {(task.created_at || task.due_date || task.completed_at) && (
        <div className={styles.taskRow2}>
          {task.created_at && (
            <span className={styles.datePill}>🕒 {fmtDate(task.created_at)}</span>
          )}
          {task.due_date && (
            <span className={`${styles.datePill} ${styles.duePill}`}>
              📅 {fmtDate(task.due_date)}
              {task.due_time ? ` — ${task.due_time.slice(0, 5)}` : ''}
            </span>
          )}
          {task.completed_at && (
            <span className={`${styles.datePill} ${styles.donePill}`}>
              ✅ {fmtDate(task.completed_at)}
            </span>
          )}
        </div>
      )}

      {/* السطر الثالث — المسؤول */}
      <div className={styles.taskRow3}>
        <span className={styles.assignLabel}>👤 المسؤول:</span>
        <select
          className={styles.assignSelect}
          value={task.assigned_to?.id || ''}
          onChange={(e) => onUpdate(task.id, 'assigned_to_id', e.target.value || null)}
          disabled={!canChange}
        >
          <option value="">— غير مسندة —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{getUserName(u)}</option>
          ))}
        </select>
      </div>

      {/* الوصف */}
      {task.description && (
        <p className={styles.taskDesc}>{task.description}</p>
      )}
    </div>
  );
}

// ─── نموذج إضافة مهمة ───────────────────────────────────────
function AddTaskForm({ deliverableId, users, onSaved, onCancel }) {
  const [title,    setTitle]    = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate,  setDueDate]  = useState('');
  const [dueTime,  setDueTime]  = useState('');
  const [file,     setFile]     = useState(null);
  const [busy,     setBusy]     = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) { setFile(null); return; }
    setFile(f.type.startsWith('image/') ? await compressImage(f) : f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('title',       title);
    fd.append('deliverable', deliverableId);
    fd.append('priority',    priority);
    if (dueDate)  fd.append('due_date',       dueDate);
    if (dueTime)  fd.append('due_time',       dueTime);
    if (assignee) fd.append('assigned_to_id', assignee);
    if (file)     fd.append('attachment',     file);
    try { await api.post('/tasks/', fd); onSaved(); }
    catch (err) { console.error(err); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.addForm}>
      <input
        className={styles.addInput}
        type="text"
        placeholder="عنوان المهمة..."
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
      />
      <div className={styles.addRow}>
        <select className={styles.addSelect} value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="medium">متوسطة</option>
          <option value="high">عالية</option>
          <option value="low">منخفضة</option>
        </select>
        <select className={styles.addSelect} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">— إسناد إلى —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{getUserName(u)}</option>)}
        </select>
        <input
          type="date"
          className={styles.addSelect}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <input
          type="time"
          className={styles.addSelect}
          value={dueTime}
          onChange={(e) => setDueTime(e.target.value)}
        />
        <input
          type="file"
          className={styles.addSelect}
          onChange={handleFile}
          accept="image/*,application/pdf"
        />
      </div>
      <div className={styles.addActions}>
        <button type="submit" className={styles.saveBtn} disabled={busy}>
          {busy ? 'جاري الحفظ...' : 'حفظ'}
        </button>
        <button type="button" className={styles.cancelBtn} onClick={onCancel}>
          إلغاء
        </button>
      </div>
    </form>
  );
}

// ─── المكوّن الرئيسي ─────────────────────────────────────────
export default function DeliverablesTable({ bookingId, deliverables, onDataChange }) {
  const { hasPermission } = useAuth();
  const [users,      setUsers]      = useState([]);
  const [addingId,   setAddingId]   = useState(null);
  const [bulkId,     setBulkId]     = useState(null);
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    if (!bookingId || !hasPermission('change_task')) return;
    api.get('/users/')
      .then((r) => {
        const staff = (r.data || []).filter(u => {
          const role = (u.role_key || u.role || '').toLowerCase();
          return role === 'admin' || role === 'dept_manager' || role === 'employee' ||
                 role === 'مدير' || role === 'مدير قسم' || role === 'موظف';
        });
        setUsers(staff);
      })
      .catch(console.error);
  }, [bookingId, hasPermission]);

  const handleTaskUpdate = async (taskId, field, value) => {
    if (!hasPermission('change_task')) return;
    try { await api.patch(`/tasks/${taskId}/`, { [field]: value }); onDataChange(); }
    catch (err) { console.error(err); }
  };

  const handleTaskDelete = async (taskId, title) => {
    if (!hasPermission('delete_task')) return;
    if (!window.confirm(`حذف المهمة: "${title}"؟`)) return;
    try { await api.delete(`/tasks/${taskId}/`); onDataChange(); }
    catch { alert('فشل الحذف.'); }
  };

  const sorted    = [...deliverables].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const canAdd    = hasPermission('add_task');
  const canChange = hasPermission('change_task');
  const canDelete = hasPermission('delete_task');

  if (sorted.length === 0) {
    return <p className={styles.empty}>لا توجد مراحل عمل لهذا الحجز.</p>;
  }

  return (
    <div className={styles.root}>

      {/* مودال المرفق */}
      {attachment && (
        <TaskAttachmentModal
          imageUrl={attachment.url}
          taskTitle={attachment.title}
          onClose={() => setAttachment(null)}
        />
      )}

      {/* مودال الإضافة المكررة */}
      <BulkAddTaskModal
        isOpen={!!bulkId}
        onClose={() => setBulkId(null)}
        onSave={() => { setBulkId(null); onDataChange(); }}
        deliverableId={bulkId}
        users={users}
      />

      {/* خط العمود */}
      <div className={styles.spine} />

      {/* المراحل */}
      {sorted.map((stage, index) => {
        const isLeft     = index % 2 === 0;
        const isDone     = isStageComplete(stage);
        const isActive   = isStageActive(stage);
        const tasks      = stage.tasks || [];

        const dotClass   = isDone ? styles.dotDone : isActive ? styles.dotActive : '';
        const dotContent = isDone ? '✓' : stage.order;

        const card = (
          <div className={`${styles.stageCard} ${isLeft ? styles.cardLeft : styles.cardRight}`}>
            {/* رأس المرحلة */}
            <div className={styles.stageHeader}>
              <div className={styles.stageNumber}>{stage.order}</div>
              <h4 className={styles.stageName}>{stage.name}</h4>
              <div className={styles.stageMeta}>
                <span className={styles.taskCount}>{tasks.length} مهمة</span>
                {stage.is_shot   && <span className={styles.statusBadge} title="اكتمل التصوير">📷</span>}
                {stage.is_edited && <span className={styles.statusBadge} title="اكتمل المونتاج">🎬</span>}
                {stage.is_posted && <span className={styles.statusBadge} title="تم النشر">📤</span>}
              </div>
            </div>

            {/* جسم المرحلة */}
            <div className={styles.stageBody}>
              {/* قائمة المهام */}
              <div className={styles.tasksList}>
                {tasks.length === 0 ? (
                  <p className={styles.noTasks}>لا توجد مهام في هذه المرحلة.</p>
                ) : (
                  tasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      users={users}
                      canChange={canChange}
                      canDelete={canDelete}
                      onUpdate={handleTaskUpdate}
                      onDelete={handleTaskDelete}
                      onAttachment={(url, title) => setAttachment({ url, title })}
                    />
                  ))
                )}
              </div>

              {/* نموذج إضافة مهمة */}
              {canAdd && (
                addingId === stage.id ? (
                  <AddTaskForm
                    deliverableId={stage.id}
                    users={users}
                    onSaved={() => { setAddingId(null); onDataChange(); }}
                    onCancel={() => setAddingId(null)}
                  />
                ) : (
                  <div className={styles.addButtons}>
                    <button
                      className={styles.addSingle}
                      type="button"
                      onClick={() => setAddingId(stage.id)}
                    >
                      + مهمة واحدة
                    </button>
                    <button
                      className={styles.addBulk}
                      type="button"
                      onClick={() => setBulkId(stage.id)}
                    >
                      + مهمة مكررة
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        );

        return (
          <div key={stage.id} className={styles.stageRow}>
            {/* يسار */}
            {isLeft ? card : <div className={styles.stageEmpty} />}

            {/* نقطة الخط المركزية */}
            <div className={`${styles.stageDot} ${dotClass}`}>
              {dotContent}
            </div>

            {/* يمين */}
            {isLeft ? <div className={styles.stageEmpty} /> : card}
          </div>
        );
      })}
    </div>
  );
}